// Bulk eligibility check runner.
//
// Wraps `POST /v2/provider/eligibility/checks/batch` (sync, capped at 25
// targets per call). Tracks per-target status as the response lands so
// the UI can flip rows in parallel and surface each target's outcome
// (the BE's `BulkCheckResultItem.status`) with the BE's error message.
//
// For volumes > 25 the BE expects either multiple sequential calls or
// the legacy scheduled batch endpoint (`postV2ProviderEligibilityBatch`,
// async with progress polling). The FE chunks at 25 here so callers
// don't have to think about it.

import { useCallback, useState } from 'react';
import { useRunBulkEligibilityChecksMutation } from '@features/patient-coverages';
import type { BulkCheckResultItem, BulkCheckSummary } from '@store/generated/eligibility';

const MAX_TARGETS_PER_CALL = 25;

export interface BulkCheckTarget {
  patient_id: string;
  encounter_id?: string;
}

/**
 * Per-target UI status. `idle` is the implicit default for any target
 * that hasn't been part of a run yet and `running` covers the request;
 * the post-run statuses are the BE's `BulkCheckResultItem.status`.
 */
export type BulkCheckTargetStatus = 'idle' | 'running' | BulkCheckResultItem['status'];

interface UseRunEligibilityChecksBatch {
  /** Map of patient_id → current per-target status. Drives row spinners. */
  statuses: Record<string, BulkCheckTargetStatus>;
  /** Map of patient_id → last result item from the BE (carries readiness, error, etc.). */
  results: Record<string, BulkCheckResultItem>;
  /** True while any batch is in-flight. */
  isRunning: boolean;
  /**
   * Run a batch. Splits into ≤ MAX_TARGETS_PER_CALL chunks under the
   * hood and resolves with the merged summary across chunks.
   */
  runBatch: (
    targets: BulkCheckTarget[],
    options?: { force?: boolean },
  ) => Promise<BulkCheckSummary>;
  /** Clear per-target status + result state (e.g. on segment change). */
  reset: () => void;
}

export function useRunEligibilityChecksBatch(): UseRunEligibilityChecksBatch {
  const [statuses, setStatuses] = useState<Record<string, BulkCheckTargetStatus>>({});
  const [results, setResults] = useState<Record<string, BulkCheckResultItem>>({});
  const [inFlightCount, setInFlightCount] = useState(0);
  const [trigger] = useRunBulkEligibilityChecksMutation();

  const runBatch = useCallback(
    async (
      targets: BulkCheckTarget[],
      options?: { force?: boolean },
    ): Promise<BulkCheckSummary> => {
      if (targets.length === 0) {
        return {
          total: 0,
          succeeded: 0,
          failed: 0,
          deduped: 0,
          self_pay_skipped: 0,
          member_id_missing: 0,
        };
      }

      // Mark every target running upfront so the UI flips all rows
      // simultaneously (matches the user's "click and watch them resolve"
      // mental model better than staggered turn-on).
      setStatuses((prev) => {
        const next = { ...prev };
        for (const t of targets) next[t.patient_id] = 'running';
        return next;
      });
      setInFlightCount((c) => c + 1);

      const chunks: BulkCheckTarget[][] = [];
      for (let i = 0; i < targets.length; i += MAX_TARGETS_PER_CALL) {
        chunks.push(targets.slice(i, i + MAX_TARGETS_PER_CALL));
      }

      const totals: BulkCheckSummary = {
        total: 0,
        succeeded: 0,
        failed: 0,
        deduped: 0,
        self_pay_skipped: 0,
        member_id_missing: 0,
      };

      try {
        for (const chunk of chunks) {
          const response = await trigger({
            bulkCheckRequest: {
              targets: chunk,
              ...(options?.force && { force: true }),
            },
          }).unwrap();

          totals.total += response.summary.total;
          totals.succeeded += response.summary.succeeded;
          totals.failed += response.summary.failed;
          totals.deduped += response.summary.deduped;
          totals.self_pay_skipped += response.summary.self_pay_skipped;
          totals.member_id_missing =
            (totals.member_id_missing ?? 0) + (response.summary.member_id_missing ?? 0);

          // Apply per-target results as they come back from this chunk.
          setResults((prev) => {
            const next = { ...prev };
            for (const item of response.results) next[item.patient_id] = item;
            return next;
          });
          setStatuses((prev) => {
            const next = { ...prev };
            for (const item of response.results) next[item.patient_id] = item.status;
            return next;
          });
        }
        return totals;
      } catch (error) {
        // Network or 5xx — flag every target in the failed bucket so the
        // user can see which rows are now in an unknown state. Per-target
        // error_message stays unset (we don't have one).
        setStatuses((prev) => {
          const next = { ...prev };
          for (const t of targets) {
            if (next[t.patient_id] === 'running') next[t.patient_id] = 'failed';
          }
          return next;
        });
        throw error;
      } finally {
        setInFlightCount((c) => Math.max(0, c - 1));
      }
    },
    [trigger],
  );

  const reset = useCallback(() => {
    setStatuses({});
    setResults({});
  }, []);

  return {
    statuses,
    results,
    isRunning: inFlightCount > 0,
    runBatch,
    reset,
  };
}
